import { type Editor, type Extensions } from "@tiptap/react";
import { cx } from "shared/utils";
import type { EditorProps } from "./core/types/editor";
import { useEditor } from "./core/hooks/use-editor";
import { useAutoExpand } from "./core/hooks/use-auto-expand";
import { EditorContentWrapper } from "./editor-content";
import { EditorContainer } from "./editor-container";

type EditorWrapperProps = EditorProps & {
  children?: (editor: Editor) => React.ReactNode;
  editable: boolean;
  extensions: Extensions;
};

export const EditorWrapper = (props: EditorWrapperProps) => {
  const {
    children,
    autoExpandConfig,
    containerClassName,
    editable,
    editorClassName = "",
    editorProps,
    extensions,
    forwardedRef,
    id,
    initialValue,
    isTouchDevice,
    mentionHandler,
    onChange,
    onEditorMount,
    onEditorFocus,
    onTransaction,
    handleEditorReady,
    autofocus,
    tabIndex,
    placeholder,
    value,
    onBlur
  } = props;

  const editor = useEditor({
    editable,
    editorClassName,
    editorProps,
    extensions,
    forwardedRef,
    isTouchDevice,
    initialValue,
    mentionHandler,
    onChange,
    onEditorMount,
    onBlur,
    onEditorFocus,
    onTransaction,
    handleEditorReady,
    autofocus,
    placeholder,
    value,
    id,
    autoExpandConfig
  });
  useAutoExpand({
    containerId: id,
    autoExpandConfig: editable ? autoExpandConfig : { autoExpand: false },
    editorInstance: editor
  });

  if (!editor) return null;

  return (
    <EditorContainer
      editor={editor}
      autoExpandConfig={autoExpandConfig}
      editorContainerClassName={cx("w-full max-w-full", containerClassName)}
      id={id}
      isTouchDevice={Boolean(isTouchDevice)}
    >
      {children?.(editor)}
      <div className="flex flex-col">
        <EditorContentWrapper editor={editor} id={id} tabIndex={tabIndex} />
      </div>
    </EditorContainer>
  );
};
